import { deleteWidget } from "$/api/customDashboard/delete-widget";
import { updateDashboard } from "$/api/customDashboard/update-dashboard";
import { getLatestInputDate } from "$/api/market-monitoring/market-monitoring.api";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import AnnualComparisonForHourlyProducts from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/AnnualComparisonForHourlyProducts";
import AverageHourlyPricingCharts from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/AverageHourlyPricingCharts";
import ElecAllMarketsChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecAllMarketsChart";
import ElecAnalysisFutureProductsChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecAnalysisFutureProductsChart";
import ElecAnalysisRSIChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecAnalysisRSIChart";
import ElecBandsChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecBandsChart";
import ElecBetweenCalendarsBasePeakDifference from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecBetweenCalendatsBasePeakDifference";
import ElecCalendarBasePeakDifference from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecCalendarBasePeakDifference";
import ElecDailyBaseAveragesBarChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecDailyBaseAveragesBarChart";
import ElecFutureProdsAveragesBarChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecFutureProdsAveragesBarChart";
import ElecSpotAvgChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecSpotAvgChart";
import ElecTreeChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityAnalysisTab/ElecTreeChart";
import DailyProductsChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityTab/DailyProductsChart";
import FutureProductsChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityTab/FutureProductsChart";
import HourlyProductsChart from "../components/pages/clients/MarketMonitoring/tabs/ElectricityTab/HourlyProductsChart";
import GasConsumptionChart from "../components/pages/clients/MarketMonitoring/tabs/FundamentalsTab/GasConsumptionChart";
import GasInjectionAndWithdrawalChart from "../components/pages/clients/MarketMonitoring/tabs/FundamentalsTab/GasInjectionAndWithdrawalChart";
import GasStorageChart from "../components/pages/clients/MarketMonitoring/tabs/FundamentalsTab/GasStorageChart";
import NationalThermalChart from "../components/pages/clients/MarketMonitoring/tabs/FundamentalsTab/NationalThermalChart";
import NuclearPlantsAvailability from "../components/pages/clients/MarketMonitoring/tabs/FundamentalsTab/NuclearPlantsAvailability";
import PowerGenerationCharts from "../components/pages/clients/MarketMonitoring/tabs/FundamentalsTab/PowerGenerationCharts";
import GasAnalysisFutureProductsChart from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasAnalysisFutureProductsChart";
import GasAnalysisRSIChart from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasAnalysisRSIChart";
import GasMarketDifference from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasAnaylsisMarketDifference";
import GasBandsChart from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasBandsChart";
import GasCalendarsMarketDifference from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasCalendarsMarketDifference";
import GasFutureProdsAveragesBarChart from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasFutureProdsAveragesBarChart";
import GasPegDailyAveragesBarChart from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasPegDailyAveragesBarChart";
import GasSpotPEGDayAheadChart from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/GasSpotPEGDayAheadChart";
import TreeChart from "../components/pages/clients/MarketMonitoring/tabs/GasAnalysisTab/TreeChart";
import GasDailyProductsChart from "../components/pages/clients/MarketMonitoring/tabs/GasTab/GasDailyProductsChart";
import GasFutureProductsChart from "../components/pages/clients/MarketMonitoring/tabs/GasTab/GasFutureProductsChart";
import BaseloadAndCO2Chart from "../components/pages/clients/MarketMonitoring/tabs/OtherMarketsTab/BaseloadAndCO2Chart";
import BrentAndCoalChart from "../components/pages/clients/MarketMonitoring/tabs/OtherMarketsTab/BrentAndCoalChart";
import CO2Chart from "../components/pages/clients/MarketMonitoring/tabs/OtherMarketsTab/CO2Chart";
import CO2SpotChart from "../components/pages/clients/MarketMonitoring/tabs/OtherMarketsTab/CO2SpotChart";
import SparkSpreadCharts from "../components/pages/clients/MarketMonitoring/tabs/OtherMarketsTab/FutureProductsSparkSpread";
import PriceVariationChart from "../components/pages/clients/MarketMonitoring/tabs/OtherMarketsTab/PriceVariationChart";
import CustomAreaGraphWidget from "../components/widgets/CustomAreaGraphWidget";
import type { DashboardWidget } from "../types/models/CustomDashboard.types";

export type WidgetTypeKey =
  | "ELECTRICITY"
  | "GAS"
  | "OTHER_MARKETS"
  | "ELECTRICITY_ANALYSIS"
  | "GAS_ANALYSIS"
  | "FUNDAMENTALS";

export type WidgetType = {
  key: string;
  name: string;
  description: string;
  type: string;
  graphType: string;
  isLargeItem: boolean;
  component: JSX.Element;
};

type CustomDashboardContextType = {
  latestInputDate: Date;
  spotLatestDate: Date;
  selectedIds: string[];
  userWidgets: DashboardWidget[];
  setSelectedIds: (ids: string[]) => void;
  setUserWidgets: (widgets: DashboardWidget[]) => void;
  handleDelete: (id: string) => void;
  getWidgets: (
    isDraggable?: boolean,
    isShowCase?: boolean,
  ) => Record<WidgetTypeKey, WidgetType[]>;
  handleUpdateDashboard: (
    id: number,
    clientId: number,
    newWdigetKeys: string[],
  ) => Promise<void>;
};

const CustomDashboardContext = createContext<CustomDashboardContextType>({
  latestInputDate: new Date(),
  spotLatestDate: new Date(),
  selectedIds: [],
  userWidgets: [],
  setSelectedIds: () => {},
  setUserWidgets: () => {},
  handleDelete: () => {},
  getWidgets: () => ({
    ELECTRICITY: [],
    GAS: [],
    OTHER_MARKETS: [],
    ELECTRICITY_ANALYSIS: [],
    GAS_ANALYSIS: [],
    FUNDAMENTALS: [],
  }),
  handleUpdateDashboard: async () => {},
});

type CustomDashboardProviderProps = {
  children: React.ReactNode;
};

const CustomDashboardProvider = ({
  children,
}: PropsWithChildren<CustomDashboardProviderProps>) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [userWidgets, setUserWidgets] = useState<DashboardWidget[]>([]);

  const { data } = useQuery({
    queryKey: ["get-latest-input-date"],
    queryFn: getLatestInputDate,
  });

  const { mutate: updateDashboardWidgets } = useMutation({
    mutationFn: updateDashboard,
    onSuccess: (data) => {
      setUserWidgets(data);
      setSelectedIds(data.map((widget) => widget.key));
    },
  });

  const { mutate } = useMutation({
    mutationFn: deleteWidget,
    onSuccess: (data) => {
      setUserWidgets(data);
      setSelectedIds(data.map((widget) => widget.key));
    },
  });

  const [latestInputDate, spotLatestDate] = useMemo(() => {
    if (!data) return [new Date(), new Date()];

    const futureLatestDate = new Date(data.futureLatestDate);
    const spotLatestDate = new Date(data.spotLatestDate);

    return [futureLatestDate, spotLatestDate];
  }, [data]);

  const handleUpdateDashboard = async (
    id: number,
    clientId: number,
    newWidgetKeys: string[],
  ) => {
    if (!newWidgetKeys.length) return;

    const widgets = newWidgetKeys.map((widgetId) => {
      const existingWidget = userWidgets.find(
        (widget) => widget.key === widgetId,
      );

      if (existingWidget) {
        return {
          id: existingWidget.id,
          key: widgetId,
          positionIndex: newWidgetKeys.indexOf(widgetId),
        };
      }

      return {
        id: undefined,
        key: widgetId,
        positionIndex: newWidgetKeys.indexOf(widgetId),
      };
    });

    updateDashboardWidgets({
      id,
      clientId,
      widgetKeys: widgets,
    });
  };

  const handleDelete = (id: string) => {
    const widgetId = userWidgets.find((widget) => widget.key === id)?.id;

    if (!widgetId) return;

    mutate(widgetId);

    setSelectedIds((prev) => prev.filter((item) => item !== id));
  };

  const handleSetSelectedIds = (ids: string[]) => {
    setSelectedIds(ids);
  };

  const getWidgets = useCallback(
    (isDraggable = true, isShowCase = false) => ({
      ELECTRICITY: [
        {
          key: "ELECTRICITY_BASE_FUTURE_PROD",
          name: "Base Cal+1",
          description: "Prix de fermeture du marché du produit Cal+1 Base.",
          type: "ELECTRICITY",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="ELECTRICITY_BASE_FUTURE_PROD"
              theme="blue"
              date={latestInputDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "ELECTRICITY_PEAK_FUTURE_PROD",
          name: "Peak Cal +1",
          description: "Prix de fermeture du marché du produit Cal+1 Peak.",
          type: "ELECTRICITY",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="ELECTRICITY_PEAK_FUTURE_PROD"
              theme="blue"
              date={latestInputDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "ELECTRICITY_AVG_SPOT_PROD",
          name: "Base Spot DA",
          description: "Prix Spot Day Ahead Base.",
          type: "ELECTRICITY",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="ELECTRICITY_AVG_SPOT_PROD"
              theme="blue"
              date={spotLatestDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "ELECTRICITY_YEAR_FutureProductsChart",
          name: "Calendaires",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures calendaires Base et Peak France.",
          type: "ELECTRICITY",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <FutureProductsChart
              id="ELECTRICITY_YEAR_FutureProductsChart"
              title="Calendaires"
              period="YEAR"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_QUARTER_FutureProductsChart",
          name: "Trimestres",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures trimestriels Base et Peak France.",
          type: "ELECTRICITY",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <FutureProductsChart
              id="ELECTRICITY_QUARTER_FutureProductsChart"
              title="Trimestres"
              period="QUARTER"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_MONTH_FutureProductsChart",
          name: "Mois",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures mensuels Base et Peak France.",
          type: "ELECTRICITY",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <FutureProductsChart
              id="ELECTRICITY_MONTH_FutureProductsChart"
              title="Mois"
              period="MONTH"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_DailyProductsChart",
          name: "Spot Day Ahead",
          description:
            "Evolution des prix et de la volatilité journalière en valeur absolue des produits Spot day ahead Base et Peak France.",
          type: "ELECTRICITY",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <DailyProductsChart
              id="ELECTRICITY_DailyProductsChart"
              selectedDate={spotLatestDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_HourlyProductsChart",
          name: "Spot Day Ahead Horaire Base et Peak",
          description: "Prix Spot Horaire Day Ahead d’un jour sélectionné.",
          type: "ELECTRICITY",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <HourlyProductsChart
              id="ELECTRICITY_HourlyProductsChart"
              defaultSelectedDate={spotLatestDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
      ],
      GAS: [
        {
          key: "GAS_PEG_FUTURE_PROD",
          name: "PEG Cal+1",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures calendaires PEG.",
          type: "GAS",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="GAS_PEG_FUTURE_PROD"
              theme="green"
              date={latestInputDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "GAS_TTF_FUTURE_PROD",
          name: "TTF Cal+1",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures trimestriels PEG.",
          type: "GAS",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="GAS_TTF_FUTURE_PROD"
              theme="green"
              date={latestInputDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "GAS_DA_SPOT_PROD",
          name: "PEG Spot Day Ahead",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures mensuels PEG.",
          type: "GAS",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="GAS_DA_SPOT_PROD"
              theme="green"
              date={spotLatestDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "GAS_YEAR_FutureProductsChart",
          name: "Calendaires",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures calendaires TTF.",
          type: "GAS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasFutureProductsChart
              id="GAS_YEAR_FutureProductsChart"
              title="Calendaires"
              period="YEAR"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_QUARTER_FutureProductsChart",
          name: "Trimestres",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures trimestriels TTF.",
          type: "GAS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasFutureProductsChart
              id="GAS_QUARTER_FutureProductsChart"
              title="Trimestres"
              period="QUARTER"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_MONTH_FutureProductsChart",
          name: "Mois",
          description:
            "Evolution des prix de fermeture des marchés et de la volatilité journalière en valeur absolue des produits futures mensuels TTF.",
          type: "GAS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasFutureProductsChart
              id="GAS_MONTH_FutureProductsChart"
              title="Mois"
              period="MONTH"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_DailyProductsChart",
          name: "Spot Day Ahead PEG",
          description:
            "Evolution des prix et de la volatilité journalière en valeur absolue du produit Spot day ahead PEG.",
          type: "GAS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasDailyProductsChart
              id="GAS_DailyProductsChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
      ],
      OTHER_MARKETS: [
        {
          key: "OTHER_MARKETS_CO2_FUTURE_PROD",
          name: "EUA (CO2) Dec+1",
          description:
            "Prix de fermeture du marché du produit Dec+1 EUA (CO2).",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="OTHER_MARKETS_CO2_FUTURE_PROD"
              theme="orange"
              date={latestInputDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_CO2_SPOT_PROD",
          name: "EUA (CO2) Spot",
          description: "Prix Spot EUA (CO2).",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="OTHER_MARKETS_CO2_SPOT_PROD"
              theme="orange"
              date={spotLatestDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_CHARBON_API2_SPOT_PROD",
          name: "Charbon API2",
          description:
            "Prix de fermeture du marché du produit charbon API2 pour livraison durant le mois en cours.",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="OTHER_MARKETS_CHARBON_API2_SPOT_PROD"
              theme="orange"
              date={spotLatestDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_BRENT_SPOT_PROD",
          name: "Brent",
          description: "Prix du pétrole Brent (livraison M+1).",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: false,
          component: (
            <CustomAreaGraphWidget
              id="OTHER_MARKETS_BRENT_SPOT_PROD"
              theme="orange"
              date={spotLatestDate}
              hasChart
              asMain
              isDraggable={isDraggable}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_BaseloadAndCO2Chart",
          name: "Base Cal+1 et PEG Cal+1 vs. EUA Dec +1",
          description:
            "Comparaison des produits futures Cal +1 : Electricité Base France, PEG  et CO2.",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <BaseloadAndCO2Chart
              id="OTHER_MARKETS_BaseloadAndCO2Chart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_BrentAndCoalChart",
          name: "Brent et Charbon API2",
          description:
            "Evolution des prix de fermeture des marchés du Brent (M+1) et du charbon API2 (M).",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <BrentAndCoalChart
              id="OTHER_MARKETS_BrentAndCoalChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_CO2Chart",
          name: "EUA (CO2) futures Dec à Dec +3",
          description:
            "Evolution des prix de fermeture des marchés futures pour les EUA pour les mois de décembre de l’année en cours et des trois années suivantes .",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <CO2Chart
              id="OTHER_MARKETS_CO2Chart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_CO2SpotChart",
          name: "EUA (CO2) Spot",
          description: "Evolution des prix Spot des EUA .",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <CO2SpotChart
              id="OTHER_MARKETS_CO2SpotChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_SparkSpreadCharts",
          name: "Clean Spark Spread Cal+1 France et Clean Spark Spread Spot France",
          description:
            "Clean Spark Spread reposant sur les produits futures calendaires France en fonction des rendements et facteurs d’émission saisis et Clean Spark Spread reposant sur les produits Spot France en fonction des rendements et facteurs d’émission saisis .",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <SparkSpreadCharts
              id="OTHER_MARKETS_SparkSpreadCharts"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "OTHER_MARKETS_PriceVariationChart",
          name: "Indices de prix Base 100 par rapport au 1er janvier d’une année donnée",
          description:
            "Indices de prix Base 100 par rapport au 1er janvier d’une année donnée.",
          type: "OTHER_MARKETS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <PriceVariationChart
              id="OTHER_MARKETS_PriceVariationChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
      ],
      ELECTRICITY_ANALYSIS: [
        {
          key: "ELECTRICITY_ANALYSIS_TreeChart",
          name: "Suivi des produits futures A+1",
          description:
            "Suivi des prix des différents produits futures de l’année suivante.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecTreeChart
              id="ELECTRICITY_ANALYSIS_TreeChart"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecAnalysisFutureProductsChart",
          name: "Calendaires et Moyennes Mobiles",
          description:
            "Suivi des produits calendaires et de leurs moyennes mobiles.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecAnalysisFutureProductsChart
              id="ELECTRICITY_ANALYSIS_ElecAnalysisFutureProductsChart"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecBandsChart",
          name: "Bandes de Bollinger - Calendaires",
          description:
            "Evolution des bandes de Bollinger pour les produits Calendaires.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecBandsChart
              id="ELECTRICITY_ANALYSIS_ElecBandsChart"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecAnalysisRSIChart",
          name: "RSI - Cal+1 à Cal+3",
          description:
            "Relative Strength Index des produits calendaires Cal+1 à Cal+3 Base ou Peak",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecAnalysisRSIChart
              id="ELECTRICITY_ANALYSIS_ElecAnalysisRSIChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecCalendarBasePeakDifference",
          name: "Ecart Calendaires Peak-Base",
          description:
            "Mesure de l’écart entre les produits Calendaires Peak et Base.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecCalendarBasePeakDifference
              id="ELECTRICITY_ANALYSIS_ElecCalendarBasePeakDifference"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecBetweenCalendarsBasePeakDifference",
          name: "Ecart par période entre les Cal+1, Cal+2 et Cal+3 Base et Peak",
          description:
            "Mesure de l’écart des  moyennes par période entre les produits Calendaires +1, +2, +3 Base et Peak .",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecBetweenCalendarsBasePeakDifference
              id="ELECTRICITY_ANALYSIS_ElecBetweenCalendarsBasePeakDifference"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecAllMarketsChart",
          name: "Ecart Calendaires France et Allemagne Base ou Peak",
          description:
            "Mesure de l’écart entre les Calendaires Base ou Peak France et Allemagne.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecAllMarketsChart
              id="ELECTRICITY_ANALYSIS_ElecAllMarketsChart"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecSpotAvgChart",
          name: "Prix et volatilité journalière Spot Day Ahead",
          description:
            "Evolution journalière du prix et de la volatilité en valeur absolue des prix Spot Day Ahead Base et Peak.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecSpotAvgChart
              id="ELECTRICITY_ANALYSIS_ElecSpotAvgChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecDailyBaseAveragesBarChart",
          name: "Spot moyen mensuel par année Base et PEG",
          description:
            "Evolution du prix spot moyen mensuel par année Base et PEG.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecDailyBaseAveragesBarChart
              id="ELECTRICITY_ANALYSIS_ElecDailyBaseAveragesBarChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_ElecFutureProdsAveragesBarChart",
          name: "Cal+1 moyen mensuel par année Base et PEG",
          description:
            "Evolution du prix Cal+1 moyen mensuel par année Base et PEG.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <ElecFutureProdsAveragesBarChart
              id="ELECTRICITY_ANALYSIS_ElecFutureProdsAveragesBarChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_AverageHourlyPricingCharts",
          name: "Prix de capture horaire et journalier par filière ",
          description:
            "Production d’électricité et prix de capture horaire et journalier par filière.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <AverageHourlyPricingCharts
              id="ELECTRICITY_ANALYSIS_AverageHourlyPricingCharts"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "ELECTRICITY_ANALYSIS_AnnualComparisonForHourlyProducts",
          name: "Spot moyen mensuel par année Base",
          description:
            "Comparaison interannuelle des prix de capture mensuel par filière de production d’électricité.",
          type: "ELECTRICITY_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <AnnualComparisonForHourlyProducts
              id="ELECTRICITY_ANALYSIS_AnnualComparisonForHourlyProducts"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
      ],
      GAS_ANALYSIS: [
        {
          key: "GAS_ANALYSIS_TreeChart",
          name: "Suivi des produits futures A+1",
          description:
            "Suivi des prix des différents produits futures de l’année suivante.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <TreeChart
              id="GAS_ANALYSIS_TreeChart"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasAnalysisFutureProductsChart",
          name: "Calendaires et Moyennes Mobiles",
          description:
            "Suivi des produits calendaires PEG et TTF et de leurs moyennes mobiles.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasAnalysisFutureProductsChart
              id="GAS_ANALYSIS_GasAnalysisFutureProductsChart"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasBandsChart",
          name: "Bandes de Bollinger - Calendaires",
          description:
            "Evolution des bandes de Bollinger pour les produits Calendaires.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasBandsChart
              id="GAS_ANALYSIS_GasBandsChart"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasMarketDifference",
          name: "Ecart entre les calendaires TTF et PEG",
          description:
            "Suivi des écarts de prix journaliers entre les calendaires TTF et PEG.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasMarketDifference
              id="GAS_ANALYSIS_GasMarketDifference"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasCalendarsMarketDifference",
          name: "Ecart entre les Cal+1, Cal+2 et Cal+3",
          description:
            "Suivi des écarts de prix journaliers entre les calendaires Cal+1 à Cal+3 PEG ou TTF.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasCalendarsMarketDifference
              id="GAS_ANALYSIS_GasCalendarsMarketDifference"
              selectedDate={latestInputDate}
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasSpotPEGDayAheadChart",
          name: "Prix et volatilité journalière Spot Day Ahead PEG",
          description:
            "Suivi du prix et de la volatilité journalière en valeur absolue Spot Day Ahead PEG.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasSpotPEGDayAheadChart
              id="GAS_ANALYSIS_GasSpotPEGDayAheadChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasAnalysisRSIChart",
          name: "RSI - Cal+1 à Cal+3",
          description:
            "Relative Strength Index des produits calendaires Cal+1 à Cal+3 PEG ou TTF.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasAnalysisRSIChart
              id="GAS_ANALYSIS_GasAnalysisRSIChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasPegDailyAveragesBarChart",
          name: "Spot moyen mensuel par année Base et PEG",
          description:
            "Evolution du prix spot moyen mensuel par année Base et PEG.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasPegDailyAveragesBarChart
              id="GAS_ANALYSIS_GasPegDailyAveragesBarChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "GAS_ANALYSIS_GasfutureProdsAveragesBarChart",
          name: "Cal+1 moyen mensuel par année Base et PEG",
          description:
            "Evolution du prix Cal+1 moyen mensuel par année Base et PEG.",
          type: "GAS_ANALYSIS",
          graphType: "LINE",
          isLargeItem: true,
          component: (
            <GasFutureProdsAveragesBarChart
              id="GAS_ANALYSIS_GasfutureProdsAveragesBarChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
      ],
      FUNDAMENTALS: [
        {
          key: "FUNDAMENTALS_NationalThermalChart",
          name: "Indicateurs thermiques France",
          description: "Indicateurs thermiques nationaux.",
          type: "FUNDAMENTALS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <NationalThermalChart
              id="FUNDAMENTALS_NationalThermalChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "FUNDAMENTALS_PowerGenerationCharts",
          name: "Production électrique par filière en France et Production électrique par filière en France",
          description:
            "Production électrique par filière au pas journalier et Production électrique par filière au pas journalier.",
          type: "FUNDAMENTALS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <PowerGenerationCharts
              id="FUNDAMENTALS_PowerGenerationCharts"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "FUNDAMENTALS_NuclearPlantsAvailability",
          name: "Disponibilité du parc nucléaire français",
          description:
            "Disponibilité historique et actuelle du parc nucléaire français.",
          type: "FUNDAMENTALS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <NuclearPlantsAvailability
              id="FUNDAMENTALS_NuclearPlantsAvailability"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "FUNDAMENTALS_GasStorageChart",
          name: "Taux de remplissage des stocks de gaz français et européens",
          description:
            "Taux de remplissage des stocks de gaz français et européens de l’année en cours et des trois années précédentes.",
          type: "FUNDAMENTALS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <GasStorageChart
              id="FUNDAMENTALS_GasStorageChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "FUNDAMENTALS_GasInjectionAndWithdrawalChart",
          name: "Stocks de gaz français : suivi des injections et des soutirages",
          description:
            "Suivi des injections et soutirages effectués depuis les sites de stockage français.",
          type: "FUNDAMENTALS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <GasInjectionAndWithdrawalChart
              id="FUNDAMENTALS_GasInjectionAndWithdrawalChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
        {
          key: "FUNDAMENTALS_GasConsumptionChart",
          name: "Consommation de gaz naturel en France",
          description:
            "Historique et valeur récente des consommations de gaz naturel en France.",
          type: "FUNDAMENTALS",
          graphType: "INDICATOR",
          isLargeItem: true,
          component: (
            <GasConsumptionChart
              id="FUNDAMENTALS_GasConsumptionChart"
              isDraggable={isDraggable}
              isShowcase={isShowCase}
            />
          ),
        },
      ],
    }),
    [latestInputDate, spotLatestDate],
  );

  return (
    <CustomDashboardContext.Provider
      value={{
        latestInputDate,
        spotLatestDate,
        selectedIds,
        userWidgets,
        setUserWidgets,
        setSelectedIds: handleSetSelectedIds,
        handleDelete,
        getWidgets,
        handleUpdateDashboard,
      }}
    >
      {children}
    </CustomDashboardContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCustomDashboard = () => useContext(CustomDashboardContext);

export default CustomDashboardProvider;
